import { useCallback, useEffect, useState } from 'react';

import { Fit, Layout } from '@rive-app/react-canvas';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import dialogPattern from '../../../../assets/dialog_pattern_desktop.png';
import type { Promotion } from '../../../../dto/promo.types';
import { useRiveMachine } from '../../../../hooks/rive/useRiveMachine';
import { useCoinsAnimation } from '../../../../hooks/useCoinsAnimation';
import { useFlag } from '../../../../services/launchDarkly';
import type { ClaimableReward } from '../../../../stores/ui/types';
import { useStores } from '../../../../stores/useStores';
import { BUCKET_PREFIX, ENVIRONMENT } from '../../../../utils/environment';
import { getParam, removeParam } from '../../../../utils/marketingHelper';
import Button from '../../../buttons/Button';
import { DialogBox } from '../../../Dialog/DialogBox';
import { ErrorMessage } from '../../../ErrorMessage/ErrorMessage';
import { PromoRewardsDisplay } from '../components/PromoRewardsDisplayV2';

import styles from './ClaimPromoGiftDialog.module.scss';

const promoLinkGiftRiv = `${BUCKET_PREFIX}${ENVIRONMENT}/assets/rive/promo_link_gift.riv`;

const ClaimPromoGiftDialogV2 = observer(() => {
  const { walletStore, portalStore, promotionStore } = useStores();
  const { t } = useTranslation();

  const [errorMsg, setErrorMsg] = useState('');
  const [isClaiming, setIsClaiming] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const { startAnimation } = useCoinsAnimation();

  const isScModeAvailable = useFlag<boolean>('sc_mode_available', true);

  const { options } = portalStore;

  const promotion = options.promotion as Promotion;
  const promotionExists = !!promotion;
  const scAmount = isScModeAvailable ? promotion?.scAmount || 0 : 0;

  const { rive, RiveComponent, setRiveViewModelBoolean, fireRiveViewModelTrigger } = useRiveMachine({
    src: promoLinkGiftRiv,
    autoplay: true,
    autoBind: true,
    layout: new Layout({ fit: Fit.Contain }),
  });

  useEffect(() => {
    if (rive) {
      setRiveViewModelBoolean('activate', true);
      setRiveViewModelBoolean('isCC', !scAmount);
      fireRiveViewModelTrigger('close');
    }
  }, [rive, setRiveViewModelBoolean, fireRiveViewModelTrigger, scAmount]);

  const removePcQueryParam = useCallback(() => {
    if (getParam(searchParams, 'pc')) {
      removeParam(searchParams, 'pc');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  const closeDialog = useCallback(() => {
    removePcQueryParam();
    promotionStore.closePromotionDialog();
  }, [promotionStore, removePcQueryParam]);

  useEffect(() => {
    if (!promotionExists) {
      closeDialog();
    }
  }, [promotionExists, closeDialog]);

  const handleSubmit = async () => {
    if (errorMsg) {
      setErrorMsg('');
    }

    setIsClaiming(true);
    fireRiveViewModelTrigger('open');

    promotionStore.sendAnalyticsEvent({
      event_action: 'click',
      click_location: 'cta',
      button_text: t('common.buttons.claim'),
      action_result: 'claim_gift',
    });
    const updatedWalletData = await promotionStore.redeemPromoCode(promotion.promoCode);
    if (updatedWalletData) {
      const { gcCoin, scCoin, totalScCoin } = updatedWalletData;
      walletStore.updateWallet({ gcCoin, scCoin, totalScCoin });
      const rewards: ClaimableReward[] = [
        { scCoin: scAmount, gcCoin: promotion.gcAmount, bundleItems: promotion.bundleItems || [] },
      ];

      startAnimation({
        rewards,
        onEnd: () => closeDialog(),
      });
    } else {
      setErrorMsg(t('promoCode.errors.claimFailed'));
      setIsClaiming(false);
    }
  };

  return (
    <DialogBox className={styles.claimPromoDialog}>
      <img alt="" className={styles.patternImage} src={dialogPattern} />
      <div className={styles.darkOverlay} />

      <div className={styles.contentWrapper}>
        <div className={styles.topSection}>
          <div className={styles.textSection}>
            {promotion.title && (
              <h2 className={styles.title} data-testid="promotion-dialog-title">
                {promotion.title}
              </h2>
            )}
            <p className={styles.subtitle}>{t('common.enjoyYourGift')}</p>
          </div>

          <div className={styles.imageSection}>
            <RiveComponent className={styles.riveAnimation} />
          </div>

          <PromoRewardsDisplay
            bundleItems={promotion.bundleItems}
            className={styles.rewardsDisplay}
            gcAmount={promotion.gcAmount}
            scAmount={scAmount}
          />
        </div>

        <div className={styles.ctaSection}>
          <Button
            className={styles.claimButton}
            dataTestId="claim-promo-gift-btn"
            disabled={isClaiming}
            onClick={handleSubmit}
          >
            {t('common.buttons.claim')}
          </Button>
          {errorMsg && <ErrorMessage className={styles.error}>{errorMsg}</ErrorMessage>}
        </div>
      </div>
    </DialogBox>
  );
});

export default ClaimPromoGiftDialogV2;
